import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { CallService } from '../services/callService';
import { authService } from '../services/authService';
import { apiClient } from '../services/apiClient';
import {
  CallDetails,
  ReviewComment,
  isAnalysisInProgress,
  isAnalysisTerminal,
} from '../types/Call';
import { CallDetailsHeader } from '../components/calldetails/CallDetailsHeader';
import { useCallContext } from '../contexts/CallContext';
import { CallTranscript } from '../components/calldetails/CallTranscript';
import { TabbedQASection } from '../components/calldetails/TabbedQASection';
import { AudioPlayer } from '../components/calldetails/AudioPlayer';
import { notificationService } from '../services/notificationService';

// Backoff schedule between polls. Analysis usually lands in the first 10-20s; stretching the
// tail keeps a slow run at ~25 requests over 5 minutes instead of 75.
const POLL_BACKOFF_MS = [4000, 4000, 4000, 8000, 8000, 15000];
const POLL_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes before we stop and hand control back to the user

const pollDelay = (attempt: number) =>
  POLL_BACKOFF_MS[Math.min(attempt, POLL_BACKOFF_MS.length - 1)];

/** 4xx other than throttling/timeout means polling will never succeed — stop rather than retry. */
const isUnrecoverablePollError = (status?: number) =>
  status !== undefined && status >= 400 && status < 500 && status !== 408 && status !== 429;

export const CallDetailsPage: React.FC = () => {
  const { callId } = useParams<{ callId: string }>();
  const navigate = useNavigate();
  const { setCallContext } = useCallContext();
  const [userRole, setUserRole] = useState<string>('');
  const [callDetails, setCallDetails] = useState<CallDetails | null>(null);
  const [reviewComments, setReviewComments] = useState<ReviewComment[]>([]);
  const [audioObjectUrl, setAudioObjectUrl] = useState<string | null>(null);

  const [fullTranscription, setFullTranscription] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [audioLoading, setAudioLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Polling state — active while analysis is running in the background.
  const [isAnalysisPolling, setIsAnalysisPolling] = useState(false);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollStartRef = useRef<number>(0);
  // Monotonic token. Clearing the timer is not enough to stop a poll: a tick that is already
  // awaiting its GET will resolve after unmount and reschedule itself. Every continuation
  // re-checks this, so a superseded or torn-down poll can never write state — which matters
  // because setCallContext targets the still-mounted provider the chatbot reads from.
  const pollGenerationRef = useRef(0);
  // Set once a poll gives up, so the auto-start effect below does not immediately
  // restart it — the call is still 'processing', which is exactly the condition
  // that effect fires on. Cleared only when the user asks for another run.
  const pollAbandonedRef = useRef(false);
  // Lets the timeout notification's "Keep waiting" action restart the loop without the
  // poll function referencing its own binding before it is declared.
  const restartPollRef = useRef<() => void>(() => {});

  const stopPolling = useCallback(() => {
    pollGenerationRef.current += 1;
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    setIsAnalysisPolling(false);
  }, []);

  const pollForAnalysisCompletion = useCallback(() => {
    if (!callId) return;

    const generation = ++pollGenerationRef.current;
    const polledCallId = callId;
    let attempt = 0;

    const isCurrent = () => generation === pollGenerationRef.current;

    const tick = async () => {
      if (!isCurrent()) return;

      if (Date.now() - pollStartRef.current > POLL_TIMEOUT_MS) {
        pollAbandonedRef.current = true;
        stopPolling();
        notificationService.warning(
          'Analysis Still Running',
          'The AI analysis has not finished after 5 minutes. It is still running on the server.',
          {
            persistent: true,
            actions: [
              {
                label: 'Keep waiting',
                action: () => {
                  pollAbandonedRef.current = false;
                  restartPollRef.current();
                },
                variant: 'outlined',
              },
            ],
          }
        );
        return;
      }

      try {
        const updated = await CallService.getCallDetails(polledCallId);
        if (!isCurrent()) return;

        setCallDetails(updated);
        setCallContext({ callDetails: updated, transcription: updated.transcriptionText || '' });

        if (!isAnalysisTerminal(updated.status)) {
          attempt += 1;
          pollTimerRef.current = setTimeout(tick, pollDelay(attempt));
          return;
        }

        stopPolling();
        if (updated.status?.toLowerCase() === 'completed' && !updated.analysisFailed) {
          notificationService.success('Analysis Complete', 'AI analysis has been regenerated.');
        } else if (updated.analysisFailed && updated.status?.toLowerCase() === 'completed') {
          // Reverted to the previous analysis after exhausting retries — 'completed' here
          // does NOT mean this run succeeded, and saying so would be a lie about the scores.
          notificationService.error(
            'Regeneration Failed',
            'AI analysis could not be regenerated. The previous scores have been kept and are unchanged.'
          );
        } else if (updated.status?.toLowerCase() === 'ai_retry_pending') {
          notificationService.warning(
            'Analysis Retrying',
            'AI analysis hit an error and is queued for an automatic retry. The scores shown are from the previous run.'
          );
        } else {
          // Never leave a failed regeneration silent: the previous scores are still on
          // screen and would otherwise read as the regenerated result.
          notificationService.error(
            'Analysis Failed',
            'AI analysis could not be regenerated. The scores shown are from the previous run.'
          );
        }
      } catch (err) {
        if (!isCurrent()) return;

        const statusCode = (err as { statusCode?: number } | undefined)?.statusCode;
        if (isUnrecoverablePollError(statusCode)) {
          // Signed out, role revoked, or the call is gone — retrying 75 times tells the
          // user nothing and ends in a misleading "taking longer than expected".
          pollAbandonedRef.current = true;
          stopPolling();
          notificationService.error(
            'Analysis Status Unavailable',
            'Could not check analysis status. Refresh the page or sign in again.'
          );
          return;
        }

        // Network blip or server error — keep polling.
        attempt += 1;
        pollTimerRef.current = setTimeout(tick, pollDelay(attempt));
      }
    };

    pollStartRef.current = Date.now();
    setIsAnalysisPolling(true);
    pollTimerRef.current = setTimeout(tick, pollDelay(0));
  }, [callId, setCallContext, stopPolling]);

  useEffect(() => {
    restartPollRef.current = pollForAnalysisCompletion;
  }, [pollForAnalysisCompletion]);

  // Start polling automatically if the call is already in an in-progress state on load
  // (e.g. user navigates here while a background analysis is running).
  useEffect(() => {
    if (
      callDetails &&
      isAnalysisInProgress(callDetails.status) &&
      !isAnalysisPolling &&
      !pollAbandonedRef.current
    ) {
      pollForAnalysisCompletion();
    }
  }, [callDetails, isAnalysisPolling, pollForAnalysisCompletion]);

  // Tear the poll down on unmount AND whenever callId changes, so a loop started for one
  // call can never keep publishing into the shared call context after we've left it.
  useEffect(() => stopPolling, [callId, stopPolling]);

  /**
   * Called by child components when the user triggers analysis regeneration.
   * Queues the work server-side then starts polling for completion.
   */
  const handleRegenerateAnalysis = useCallback(async () => {
    if (!callId || isAnalysisPolling) return;

    // Flip to the running state before awaiting the POST — otherwise the button stays
    // enabled for the whole round trip and a second click fires a second request.
    setIsAnalysisPolling(true);
    try {
      await CallService.regenerateAnalysis(callId);
    } catch {
      // Already surfaced by notificationService in the service layer. Swallow here so the
      // rejection doesn't escape an onClick handler, and don't poll for work never queued.
      setIsAnalysisPolling(false);
      return;
    }
    pollAbandonedRef.current = false;
    // Optimistically flip status so the UI shows the in-progress state immediately
    setCallDetails((prev) => (prev ? { ...prev, status: 'processing' } : prev));
    pollForAnalysisCompletion();
  }, [callId, isAnalysisPolling, pollForAnalysisCompletion]);

  /**
   * After CAD fields are applied, re-read the call so the header shows them. Applying an operator
   * can move the call outside a team-scoped Supervisor's reach, so a 403 here is an expected
   * outcome of the apply, not a failure: say so and leave the call.
   */
  const handleCadRecordApplied = useCallback(
    async (operatorChanged: boolean) => {
      if (!callId) return;
      try {
        const updated = await CallService.getCallDetailsIfVisible(callId);
        if (!updated) {
          notificationService.warning(
            'Call No Longer Available',
            operatorChanged
              ? 'The CAD record was applied. With the new operator, this call is outside your access, so you have been returned to the call list.'
              : 'The CAD record was applied, but this call is no longer available to you, so you have been returned to the call list.'
          );
          navigate('/');
          return;
        }
        setCallDetails(updated);
        setCallContext({ callDetails: updated, transcription: updated.transcriptionText || '' });
      } catch {
        if (operatorChanged) {
          // Access may just have been withdrawn and could not be confirmed; do not keep the call
          // on screen on the strength of an authorization that may no longer hold.
          notificationService.warning(
            'Call Not Refreshed',
            'The CAD record was applied, but your access to this call could not be confirmed after the operator changed. You have been returned to the call list.'
          );
          navigate('/');
          return;
        }
        notificationService.error(
          'Call Not Refreshed',
          'The CAD record was applied, but the call could not be reloaded. Refresh the page to see the new values.'
        );
      }
    },
    [callId, navigate, setCallContext]
  );

  // Revoke object URL on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (audioObjectUrl) URL.revokeObjectURL(audioObjectUrl);
    };
  }, [audioObjectUrl]);

  useEffect(() => {
    const loadCallDetails = async () => {
      if (!callId) {
        setError('Call ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // Load call details, comments, and user role in parallel.
        // Transcription is already included in the call details response (TranscriptionText field),
        // so no separate /transcription call is needed.
        // Load details first (critical), comments separately (non-critical).
        // A comments failure should never prevent the page from rendering.
        const [details, comments, user] = await Promise.all([
          CallService.getCallDetails(callId),
          CallService.getReviewComments(callId).catch(() => [] as ReviewComment[]),
          authService.getUserAsync().catch(() => null),
        ]);
        setUserRole(user?.role || '');

        setCallDetails(details);
        setReviewComments(comments);

        // Use transcription from the main call details response
        if (details.status === 'completed' && details.transcriptionText) {
          setFullTranscription(details.transcriptionText);
        }

        // Set call context for chatbot
        setCallContext({
          callDetails: details,
          transcription: details.transcriptionText || '',
        });
      } catch (_err) {
        setError(_err instanceof Error ? _err.message : 'Failed to load call details');
      } finally {
        setLoading(false);
      }

      // Load audio in background — don't block page render on blob download.
      // Audio files can be 20-50MB; streaming them through the API proxy takes
      // several seconds and was the main cause of 7-10s page load times.
      try {
        const audioResponse = await apiClient.get(`/api/calls/${callId}/audio-stream`, {
          responseType: 'blob',
          timeout: 120000,
        });
        const url = URL.createObjectURL(audioResponse.data);
        setAudioObjectUrl(url);
      } catch {
        // Audio not available — player will show "not available" message
      } finally {
        setAudioLoading(false);
      }
    };

    loadCallDetails();

    // Cleanup: clear call context when component unmounts
    return () => {
      setCallContext(undefined);
    };
  }, [callId, setCallContext]);

  const handleAddComment = async (comment: string) => {
    if (!callId) return;

    try {
      const newComment = await CallService.addReviewComment(callId, comment);
      setReviewComments((prev) => [...prev, newComment]);
    } catch (_err) {
      setError(_err instanceof Error ? _err.message : 'Failed to add comment');
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mb: 2 }}>
          Back to Home
        </Button>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!callDetails) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mb: 2 }}>
          Back to Home
        </Button>
        <Alert severity="warning">Call not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with back button and call info */}
      <CallDetailsHeader
        callDetails={callDetails}
        onBack={handleBack}
        onCallDetailsUpdate={setCallDetails}
        userRole={userRole}
      />

      <Box display="grid" gridTemplateColumns={{ xs: '1fr', lg: '1fr 1fr' }} gap={3} sx={{ mt: 3 }}>
        {/* Left column - Audio Player and Transcript */}
        <Stack spacing={3}>
          <AudioPlayer
            audioUrl={audioObjectUrl || undefined}
            duration={callDetails.processingTime}
            isLoadingAudio={audioLoading}
          />
          <CallTranscript
            transcript={fullTranscription || callDetails.transcriptionText}
            loading={false}
            audioFileName={callDetails.audioFileName}
          />
        </Stack>

        {/* Right column - Tabbed QA Section */}
        <TabbedQASection
          callDetails={callDetails}
          reviewComments={reviewComments}
          onScoresUpdated={(updatedCallDetails) => setCallDetails(updatedCallDetails)}
          onAddComment={handleAddComment}
          onRegenerateAnalysis={handleRegenerateAnalysis}
          isAnalysisPolling={isAnalysisPolling}
          userRole={userRole}
          onCallRecordApplied={handleCadRecordApplied}
        />
      </Box>
    </Box>
  );
};
